import { endOfDay, startOfDay, subDays } from "date-fns";

export type UpdatesInboxMode = "unread" | "all";

export type UpdatesHistoryRange =
  | { kind: "today" }
  | { kind: "yesterday" }
  | { kind: "last_7_days" }
  | { kind: "last_30_days" };

export const DEFAULT_UPDATES_INBOX_MODE: UpdatesInboxMode = "unread";
export const DEFAULT_UPDATES_HISTORY_RANGE: UpdatesHistoryRange = {
  kind: "last_7_days",
};

export const UPDATES_HISTORY_PRESETS: {
  kind: UpdatesHistoryRange["kind"];
  label: string;
}[] = [
  { kind: "today", label: "Today" },
  { kind: "yesterday", label: "Yesterday" },
  { kind: "last_7_days", label: "Last 7 days" },
  { kind: "last_30_days", label: "Last 30 days" },
];

export function historyRangeStatusLabel(range: UpdatesHistoryRange): string {
  return UPDATES_HISTORY_PRESETS.find((preset) => preset.kind === range.kind)?.label
    ?? "Last 7 days";
}

/**
 * Map All-mode Show-from presets to inclusive `alert_date` bounds for
 * GET /notify/inbox `date_from` / `date_to` (ISO datetime).
 * Rolling windows are inclusive local calendar days ending today
 * (7 → today plus 6 prior; 30 → today plus 29 prior).
 */
export function historyRangeToAlertDateBounds(
  range: UpdatesHistoryRange,
  now = new Date(),
): { date_from: string; date_to: string } {
  if (range.kind === "today") {
    return {
      date_from: startOfDay(now).toISOString(),
      date_to: endOfDay(now).toISOString(),
    };
  }
  if (range.kind === "yesterday") {
    const day = subDays(now, 1);
    return {
      date_from: startOfDay(day).toISOString(),
      date_to: endOfDay(day).toISOString(),
    };
  }
  if (range.kind === "last_7_days") {
    return {
      date_from: startOfDay(subDays(now, 6)).toISOString(),
      date_to: endOfDay(now).toISOString(),
    };
  }
  return {
    date_from: startOfDay(subDays(now, 29)).toISOString(),
    date_to: endOfDay(now).toISOString(),
  };
}

export const UPDATES_HISTORY_EMPTY_TITLE = "Nothing in this period";
export const UPDATES_HISTORY_EMPTY_CAPTION = "Nothing happened in this period.";

export function updatesIdleEmptyCopy(
  inboxMode: UpdatesInboxMode,
  unread: { title?: string; caption: string },
): { title: string; caption: string } {
  if (inboxMode === "all") {
    return { title: UPDATES_HISTORY_EMPTY_TITLE, caption: UPDATES_HISTORY_EMPTY_CAPTION };
  }
  return { title: unread.title ?? "You're caught up here", caption: unread.caption };
}
